import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../../../assets/css/customer/shop/screens/ProductListing.css";
import MyNavbar from "../../../components/customer/navigationbar/MyNavbar";
import FilterSidebar from "../../../components/customer/shop/FilterSidebar";
import ProductCard from "../../../components/customer/shop/ProductCard";
import SearchBar from "../../../components/customer/shop/SearchBar";
import AppContext from "../../../context/AppContext";
import Footer from "../../../subscreen/customer/footer/Footer";
const ProductListing = () => {
  const { baseUrl } = useContext(AppContext);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("featured");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const viewAllProducts = () => {
    axios
      .get(`${baseUrl}/customer/products/viewProducts`)
      .then((response) => {
        console.log(response.data);
        setProducts(response.data.products);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    viewAllProducts();
  }, []);

  // ✅ CHANGE 3: Read ?category=X from the URL
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const categoryFromUrl = searchParams.get("category");
    if (categoryFromUrl) {
      // Pre-populate the category filter from the URL query param
      setActiveFilters((prev) => ({ ...prev, category: categoryFromUrl }));
    }
  }, [searchParams]); // Re-runs if the URL param changes (e.g. user clicks another category)

  // Apply all filters
  useEffect(() => {
    let result = [...(products || [])];

    // Search filter
    if (searchQuery) {
      result = result.filter(
        (product) =>
          product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          product.description
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          product.category.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    // Category filter
    if (activeFilters.category) {
      result = result.filter(
        (product) => product.category === activeFilters.category,
      );
    }

    // Price range filter
    if (activeFilters.priceRange) {
      result = result.filter(
        (product) =>
          product.price >= activeFilters.priceRange.min &&
          product.price <= activeFilters.priceRange.max,
      );
    }

    // Discount filter
    if (activeFilters.discount) {
      result = result.filter(
        (product) => product.discount >= activeFilters.discount.min,
      );
    }

    // Apply sorting
    result = applySorting(result, sortBy);

    setFilteredProducts(result);
  }, [products, activeFilters, searchQuery, sortBy]);

  // Sorting logic
  const applySorting = (productList, sortType) => {
    const sorted = [...productList];
    switch (sortType) {
      case "price-asc":
        return sorted.sort((a, b) => a.price - b.price);
      case "price-desc":
        return sorted.sort((a, b) => b.price - a.price);
      case "rating":
        return sorted.sort((a, b) => b.rating - a.rating);
      case "popular":
        return sorted.sort((a, b) => b.reviews - a.reviews);
      case "newest":
        return sorted.sort((a, b) => b.id - a.id);
      case "featured":
      default:
        return sorted.sort((a, b) => {
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          return 0;
        });
    }
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    if (filterType === "clearAll") {
      setActiveFilters({});
      return;
    }

    setActiveFilters((prev) => {
      const updated = { ...prev };
      if (updated[filterType] === value) {
        delete updated[filterType];
      } else {
        updated[filterType] = value;
      }
      return updated;
    });
  };

  const handleSearch = (query) => setSearchQuery(query);
  const handleSort = (sortType) => setSortBy(sortType);
  const toggleMobileFilters = () => setShowMobileFilters(!showMobileFilters);

  return (
    <div className="pl-product-listing-screen">
      <MyNavbar />

      {/* Page Header */}
      <div className="pl-page-header">
        <div className="pl-header-content">
          <h1 className="pl-page-title">Our Furniture Collection</h1>
          <p className="pl-page-subtitle">
            Discover premium quality furniture for every room in your home
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="pl-content-wrapper">
        {/* Mobile Filter Toggle */}
        <button
          className="pl-mobile-filter-toggle"
          onClick={toggleMobileFilters}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          Filters
          {Object.keys(activeFilters).length > 0 && (
            <span className="pl-filter-badge">
              {Object.keys(activeFilters).length}
            </span>
          )}
        </button>

        <div className="pl-layout">
          {/* Sidebar */}
          <div
            className={`pl-sidebar ${showMobileFilters ? "pl-sidebar-open" : ""}`}
          >
            <button className="pl-mobile-close" onClick={toggleMobileFilters}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <FilterSidebar
              onFilterChange={handleFilterChange}
              activeFilters={activeFilters}
            />
          </div>

          {/* Products Section */}
          <div className="pl-products-section">
            <SearchBar
              onSearch={handleSearch}
              onSort={handleSort}
              totalProducts={filteredProducts.length}
            />

            {filteredProducts.length > 0 ? (
              <div className="pl-products-grid">
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="pl-no-results">
                <svg
                  className="pl-no-results-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3 className="pl-no-results-title">No products found</h3>
                <p className="pl-no-results-text">
                  Try adjusting your filters or search query
                </p>
                <button
                  className="pl-clear-filters-btn"
                  onClick={() => {
                    setActiveFilters({});
                    setSearchQuery("");
                  }}
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showMobileFilters && (
        <div className="pl-mobile-overlay" onClick={toggleMobileFilters}></div>
      )}

      <Footer />
    </div>
  );
};

export default ProductListing;
